// imports
import { FC, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, helper
import { HTTP_STATUS } from 'constants/index';
import { renderProductSubTypes } from 'lib/helper';
import { SubProductTypeSelectProps } from 'interfaces';
import { SubProductTypesPayload, useFindAllSubProductTypeSelectorLazyQuery } from 'generated/graphql';

const SubProductTypeSelect: FC<SubProductTypeSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  productTypeId,
  margin = 'dense',
  isClearable = false,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<SubProductTypesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return renderProductSubTypes(options);
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllSubProductTypeSelectorLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllSubProductType } = data;
      const { data: subProductTypes, response, pagination } = findAllSubProductType || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((subProductTypes as SubProductTypesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllSubProductTypeInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(productTypeId && { productTypeId }),
            ...(typeof isActive === 'boolean' && { isActive }),
          },
        },
      });
    },
    [fetchSelectorData, isActive, productTypeId],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default SubProductTypeSelect;
